import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  Play,
  Pause,
  Trash,
  ArrowsClockwise,
  Table,
  ArrowCounterClockwise,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import EditConnectionDialog from "@/components/EditConnectionDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Connections() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/connections");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (c) => {
    const next = c.status === "active" ? "paused" : "active";
    await api.patch(`/connections/${c.connection_id}`, { status: next });
    toast.success(next === "active" ? "Connection resumed" : "Connection paused");
    load();
  };

  const del = async (c) => {
    await api.delete(`/connections/${c.connection_id}`);
    toast.success("Connection deleted");
    load();
  };

  const pollNow = async (c) => {
    toast.loading("Polling…", { id: c.connection_id });
    try {
      const { data } = await api.post(`/connections/${c.connection_id}/poll-now`);
      toast.success(`Poll complete • ${data.sent} sent`, { id: c.connection_id });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Poll failed", { id: c.connection_id });
    }
  };

  const resetDedup = async (c) => {
    if (!window.confirm(`Reset dedup for "${c.label}"? This will re-consider every row in the sheet on the next poll — previously invited customers may be invited again.`)) return;
    try {
      const { data } = await api.post(`/connections/${c.connection_id}/reset-dedup`);
      toast.success(`Cleared ${data.cleared} dedup entries — next poll will re-scan`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-[1400px]">
      <header className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-2">Connections</div>
          <h1 className="font-display text-5xl font-black tracking-tight" data-testid="connections-title">Connected sheets</h1>
        </div>
        <Link
          to="/connections/new"
          data-testid="connections-new-btn"
          className="inline-flex items-center gap-2 bg-[#F58021] hover:bg-[#D6691A] text-white px-5 h-11 rounded-md font-semibold transition-colors"
        >
          <Plus weight="bold" size={16} /> New connection
        </Link>
      </header>

      <div className="bg-white border border-stone-200">
        {loading ? (
          <div className="p-10 text-neutral-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center">
            <Table weight="duotone" size={44} className="mx-auto text-neutral-400 mb-3" />
            <div className="font-display text-2xl font-bold mb-2">No connections yet</div>
            <p className="text-neutral-500 text-sm mb-6 max-w-md mx-auto">
              Wire up your first Google Sheet. Whenever a new row lands, we send a review invite through your MySaaS workflow.
            </p>
            <Link
              to="/connections/new"
              data-testid="empty-new-connection"
              className="inline-flex items-center gap-2 bg-[#F58021] hover:bg-[#D6691A] text-white px-5 h-11 rounded-md font-semibold"
            >
              <Plus weight="bold" size={16} /> Add connection
            </Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-neutral-500 border-b border-stone-200">
              <tr>
                <th className="text-left py-3 px-6">Label / Sheet</th>
                <th className="text-left py-3 px-3">Business / Location</th>
                <th className="text-left py-3 px-3">Template</th>
                <th className="text-left py-3 px-3">Status</th>
                <th className="text-left py-3 px-3">Last polled</th>
                <th className="text-right py-3 px-6">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="connections-body">
              {items.map((c) => (
                <tr key={c.connection_id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50">
                  <td className="py-4 px-6">
                    <div className="font-semibold">{c.label}</div>
                    <div className="text-xs text-neutral-500 font-mono-tech truncate max-w-[280px]">{c.spreadsheet_name} · {c.sheet_tab}</div>
                  </td>
                  <td className="py-4 px-3 font-mono-tech text-xs text-neutral-700">
                    <div>{c.business_id}</div>
                    <div className="text-neutral-500">{c.location_id}</div>
                  </td>
                  <td className="py-4 px-3 font-mono-tech text-xs">
                    <div className="flex items-center gap-2 flex-wrap">
                      {c.template_channel && (
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                          c.template_channel === "email" ? "bg-sky-100 text-sky-800 border border-sky-200" :
                          c.template_channel === "sms" ? "bg-emerald-100 text-emerald-800 border border-emerald-200" :
                          c.template_channel === "whatsapp" ? "bg-green-100 text-green-800 border border-green-200" :
                          "bg-neutral-100 text-neutral-700 border border-neutral-200"
                        }`}>{c.template_channel}</span>
                      )}
                      <span className="font-sans font-medium">{c.template_name || c.template_id}</span>
                    </div>
                  </td>
                  <td className="py-4 px-3"><StatusBadge status={c.status} /></td>
                  <td className="py-4 px-3 text-xs text-neutral-600 font-mono-tech">
                    {c.last_polled_at ? new Date(c.last_polled_at).toLocaleString() : "—"}
                    <div className="text-neutral-400">rows seen: {c.last_row_count}</div>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <div className="inline-flex items-center gap-1">
                      <IconBtn testid={`poll-${c.connection_id}`} onClick={() => pollNow(c)} title="Poll now"><ArrowsClockwise weight="bold" size={15} /></IconBtn>
                      <IconBtn testid={`reset-${c.connection_id}`} onClick={() => resetDedup(c)} title="Reset dedup (rescan all rows)"><ArrowCounterClockwise weight="bold" size={15} /></IconBtn>
                      <EditConnectionDialog connection={c} onSaved={load} />
                      <IconBtn testid={`toggle-${c.connection_id}`} onClick={() => toggle(c)} title={c.status === "active" ? "Pause" : "Resume"}>
                        {c.status === "active" ? <Pause weight="bold" size={15} /> : <Play weight="bold" size={15} />}
                      </IconBtn>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`delete-${c.connection_id}`} className="p-2 hover:bg-red-50 hover:text-red-700 rounded transition-colors"><Trash weight="bold" size={15} /></button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this connection?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will stop polling and remove <b>{c.label}</b>. History entries stay.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction data-testid={`confirm-delete-${c.connection_id}`} onClick={() => del(c)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, title, testid }) {
  return (
    <button
      data-testid={testid}
      title={title}
      onClick={onClick}
      className="p-2 hover:bg-neutral-100 rounded transition-colors"
    >
      {children}
    </button>
  );
}
